<template>
  <div :class="getMainClasses()">
    <div
      v-show="isUserMenuOpen"
      aria-label="overlay"
      class="tw:fixed tw:bottom-0 tw:right-0 tw:top-0 tw:z-30 tw:sm:block tw:hidden tw:w-full tw:h-full"
    />
    <div
      :class="getClasses()"
      class="nav-user-list-item tw:top-0 tw:block tw:w-auto tw:bg-white tw:z-30 tw:h-full tw:md:h-auto"
    >
      <NavUserMenuButton
        :class="{ 'tw:md:hidden': isUserMenuOpen }"
        class="tw:w-full tw:outline-hidden tw:focus:outline-hidden tw:md:px-2 tw:md:py-2.5 tw:px-0 tw:h-full tw:md:h-auto"
        :full-name="fullName"
        :last-name="lastName"
        :language="language"
        @toggleUserMenu="toggleUserMenu"
        :is-user-menu-open="isUserMenuOpen"
        :show-welcome-text-on-mobile="false"
      />
      <div
        v-if="isUserMenuOpen"
        v-click-outside="closeMenuIfAllowed"
        id="user-navigation"
        :class="language === 'ja-JP' ? 'tw:md:w-[150px]' : 'tw:md:w-nav-user'"
        class="tw:bg-white tw:w-full tw:z-50 tw:shadow-lg nav-user-menu-options tw:absolute tw:md:left-auto tw:left-0 tw:top-0 tw:rounded-lg"
      >
        <NavUserMenuButton
          class="tw:w-full tw:outline-hidden tw:focus:outline-hidden tw:px-2 tw:py-2.5 tw:pt-2.5 tw:h-full tw:md:h-auto tw:items-center tw:text-left"
          :full-name="fullName"
          :last-name="lastName"
          :language="language"
          @toggleUserMenu="toggleUserMenu"
          :is-user-menu-open="isUserMenuOpen"
          :show-welcome-text-on-mobile="true"
        />
        <hr class="tw:md:mx-3 tw:bg-gray-300" />
        <div class="tw:my-1">
          <router-link
            data-testid="nav-user-menu-settings"
            data-tooltip
            to="/settings"
            class="tw:mx-2 tw:block tw:hover:bg-gray-200 tw:hover:no-underline tw:active:bg-gray-300 tw:px-1 tw:py-2.5 tw:md:text-gray-700 tw:hover:text-gray-800"
            @click="toggleUserMenu"
          >
            <div class="tw:flex tw:items-center">
              <SettingsGray class="tw:fill-gray-800 tw:mr-1 tw:size-6" />
              <span class="tw:text-base tw:md:text-sm tw:text-gray-800">{{ $t('userMenu.settings') }}</span>
            </div>
          </router-link>
          <router-link
            v-if="showBillingTab"
            data-testid="nav-user-menu-billing"
            data-tooltip
            to="/billing"
            class="tw:mx-2 tw:block tw:hover:bg-gray-200 tw:hover:no-underline tw:active:bg-gray-300 tw:relative tw:px-1 tw:py-2.5 tw:text-gray-700 tw:hover:text-gray-800"
            :class="{ 'tw:bg-gray-200': shouldKeepMenuOpen }"
            @click="toggleUserMenu"
          >
            <div class="tw:flex tw:items-center">
              <RcCommissionsIcon class="tw:fill-gray-800 tw:mr-1 tw:size-6" />
              <span class="tw:text-base tw:md:text-sm tw:text-gray-800">{{ $t('tbs_settings_billing') }}</span>
            </div>
          </router-link>
          <router-link
            data-testid="nav-user-menu-organizations"
            data-tooltip
            to="/organisations"
            class="tw:mx-2 tw:block tw:hover:bg-gray-200 tw:hover:no-underline tw:active:bg-gray-300 tw:relative tw:px-1 tw:py-2.5 tw:text-gray-700 tw:hover:text-gray-800"
            @click="toggleUserMenu"
          >
            <div class="tw:flex tw:items-center">
              <Organization class="tw:fill-gray-800 tw:mr-1 tw:size-6" />
              <span class="tw:text-base tw:md:text-sm tw:text-gray-800">{{ $t('userMenu.organizationSwitcher') }}</span>
            </div>
          </router-link>
        </div>

        <div class="tw:my-1">
          <router-link
            v-if="isAdminToolAuthority"
            data-testid="nav-user-menu-admin-tool"
            data-tooltip
            to="/admin-tool"
            class="tw:mx-2 tw:block tw:hover:bg-gray-200 tw:hover:no-underline tw:active:bg-gray-300 tw:relative tw:px-1 tw:py-2.5 tw:text-gray-700 tw:hover:text-gray-800"
            @click="toggleUserMenu"
          >
            <div class="tw:flex tw:items-center">
              <Tool class="tw:fill-gray-800 tw:mr-1 tw:size-6" />
              <span class="tw:text-base tw:md:text-sm tw:text-gray-800">{{ 'Admin tool' }}</span>
            </div>
          </router-link>

          <a
            data-testid="nav-user-menu-support-center"
            target="_blank"
            :href="supportCenterUrl({ language })"
            class="tw:md:hidden tw:block tw:hover:bg-gray-200 tw:active:bg-gray-300 tw:hover:no-underline tw:relative tw:px-1 tw:py-2.5 tw:text-gray-700 tw:hover:text-gray-800"
            @click="toggleUserMenu"
          >
            <div class="tw:flex tw:items-center tw:justify-between">
              <div class="tw:items-center tw:flex">
                <QuestionMark class="tw:fill-gray-800 tw:mr-1 tw:size-6" />
                <span class="tw:text-base tw:md:text-sm tw:text-gray-800">{{ $t('navigation.supportCenter') }}</span>
              </div>
              <NewTab class="tw:fill-none tw:stroke-gray-800 tw:size-6" />
            </div>
          </a>
        </div>
        <hr class="tw:md:mx-3 tw:bg-gray-300" />
        <div class="tw:p-2 tw:pt-0 tw:mt-1">
          <button
            id="signOutButton"
            data-testid="nav-user-menu-sign-out"
            data-tooltip
            class="tw:block tw:hover:bg-gray-200 tw:active:bg-gray-300 tw:hover:no-underline tw:relative tw:px-1 tw:py-2.5 tw:text-gray-700 tw:w-full tw:hover:text-gray-800"
            @click="logout"
            @keydown.enter="logout"
          >
            <div class="tw:flex tw:items-center">
              <ArrowLeft class="tw:mr-1 tw:fill-none tw:size-6" />
              <span class="tw:text-base tw:md:text-sm tw:text-gray-800">{{ $t('userMenu.signOut') }}</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import NavUserMenuButton from '@/components/dashboard/nav/NavUserMenuButton.vue'
import SettingsGray from '@/components/icons/SettingsGray.svg?component'
import Tool from '@/components/icons/Tool.svg?component'
import QuestionMark from '@/components/icons/QuestionMark.svg?component'
import ArrowLeft from '@/components/icons/ArrowLeft.svg?component'
import Organization from '@/components/icons/Organization.svg?component'
import NewTab from '@/components/icons/NewTab.svg?component'
import RcCommissionsIcon from '@/components/icons/RcCommissionsIcon.svg?component'

import { getSupportCenterUrl } from '@/utils/Utility.ts'
import { selectedAccommodationId, useUserDetails } from '@/layouts/queries'
import { useCampaign } from '@/components/rateConnect/queries'
import { useStudioPlusCampaign, useZuoraAccount } from '@/components/payment/queries'
import { authorities } from '@/constants/authorities.ts'
import { useSessionValues } from '@/composables/useSessionValues'

const store = useStore()
const route = useRoute()
const { hasAdminOrSupport, userId, language, userAuthorities } = useSessionValues()
const { userDetails, fullName } = useUserDetails(userId)

const isUserMenuOpen = ref(false)
const lastName = computed(() => userDetails.value?.lastName)
const { hasNoBillingInfo } = useZuoraAccount()
const hasPaymentMethods = computed(() => !hasNoBillingInfo.value)
const shouldKeepMenuOpen = computed(() => route.query?.navigation_open === 'true')

const showBillingTab = computed(() => {
  if (!hasAdminOrSupport.value) return false
  return isPaidClient.value || hasPaymentMethods.value
})

const { campaignsData } = useCampaign(selectedAccommodationId)
const { studioPlusCampaign } = useStudioPlusCampaign(selectedAccommodationId)
const isPaidClient = computed(
  () =>
    Boolean(studioPlusCampaign.value) ||
    (Array.isArray(campaignsData.value) ? campaignsData.value.length > 0 : Boolean(campaignsData.value))
)

const logout = () => {
  window.sessionStorage?.removeItem?.('cpa_upsell_skip')
  store.dispatch('session/logout')
}

const toggleUserMenu = () => {
  if (isUserMenuOpen.value && shouldKeepMenuOpen.value) {
    return
  }
  isUserMenuOpen.value = !isUserMenuOpen.value
}

const isAdminToolAuthority = computed(() => {
  return userAuthorities.value.includes(authorities['ADMIN_TOOL_AUTHORITY'])
})

const closeMenuIfAllowed = () => {
  if (!shouldKeepMenuOpen.value) {
    isUserMenuOpen.value = false
  }
}

const getMainClasses = () => {
  const classes = []
  if (language.value === 'ja-JP') {
    classes.push('tw:md:w-[150px]')
  } else {
    classes.push('tw:md:w-nav-user')
  }

  if (!fullName.value) {
    classes.push('tw:invisible')
  }

  return classes.join(', ')
}

const getClasses = () => {
  const classes = []
  if (isUserMenuOpen.value) {
    classes.push('tw:shadow-lg tw:md:left-auto tw:left-0 tw:w-full')
  }
  if (language.value === 'ja-JP') {
    classes.push('tw:md:w-[150px]')
  } else {
    classes.push('tw:md:w-nav-user')
  }

  return classes.join(', ')
}
const supportCenterUrl = locale =>
  getSupportCenterUrl(locale.language, userId.value, selectedAccommodationId.value, isPaidClient.value)

watch(
  () => route.query,
  query => {
    if (query?.navigation_open === 'true') {
      isUserMenuOpen.value = true
    } else {
      isUserMenuOpen.value = false
    }
  },
  { immediate: true, deep: true }
)
</script>
<style scoped>
.nav-user-list-item {
  right: 20px;
}
.nav-user-menu-options {
  clip-path: inset(0px -15px -15px -15px);
}
</style>
